import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  FiArrowRight,
  FiChevronDown,
  FiLogOut,
  FiMenu,
  FiPlus,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { user, logout, openLogin, openRegister } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const searchRef = useRef(null);
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
  }, [location]);

  useEffect(() => {
    const fn = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target))
        setSearchOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  useEffect(() => {
    const fn = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/websites?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const links = [
    { to: "/", label: "Home" },
    { to: "/categories", label: "Categories" },
    { to: "/websites", label: "Directory" },
    { to: "/top-50", label: "Top 50" },
  ];

  const active = (p) => location.pathname === p;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          transparent
            ? "bg-transparent"
            : "bg-white border-b border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
        }`}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-[3.75rem]">
            {/* ── Logo only — no text ── */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="/logo.png"
                alt="SiteVault"
                className={`h-9 w-auto object-contain transition-all duration-300 ${
                  transparent ? "brightness-0 invert" : ""
                }`}
              />
            </Link>

            {/* ── Center Nav ── */}
            <nav
              className={`hidden lg:flex items-center rounded-xl p-1 transition-all duration-300 ${
                transparent
                  ? "bg-white/[0.06] border border-white/[0.08]"
                  : "bg-slate-100/80 border border-slate-200/50"
              }`}
            >
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`px-4 py-1.5 rounded-lg text-[13px] font-semibold transition-all duration-200 ${
                    active(l.to)
                      ? transparent
                        ? "text-white bg-white/15 shadow-sm"
                        : "text-indigo-700 bg-white shadow-sm"
                      : transparent
                        ? "text-white/50 hover:text-white/80"
                        : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {/* ── Right Actions ── */}
            <div className="flex items-center gap-1.5">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-all duration-300 ${
                  transparent
                    ? "text-white/50 hover:text-white/80 hover:bg-white/[0.06] border border-white/[0.08]"
                    : "text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200/70 border border-slate-200/80"
                }`}
              >
                <FiSearch size={14} />
                <span className="hidden xl:inline">Search</span>
                <kbd
                  className={`hidden xl:inline text-[10px] font-bold px-1.5 py-0.5 rounded ml-2 ${
                    transparent
                      ? "bg-white/10 text-white/40"
                      : "bg-white text-slate-400 border border-slate-200"
                  }`}
                >
                  ⌘K
                </kbd>
              </button>

              {/* Desktop auth/actions */}
              <div className="hidden md:flex items-center gap-1.5">
                {user ? (
                  <>
                    <Link
                      to="/submit"
                      className={`flex items-center gap-1.5 text-[13px] font-semibold px-3.5 py-1.5 rounded-lg transition-all duration-300 ${
                        transparent
                          ? "text-white bg-white/10 hover:bg-white/20 border border-white/10"
                          : "text-white bg-indigo-600 hover:bg-indigo-700"
                      }`}
                    >
                      <FiPlus size={14} /> Submit
                    </Link>

                    <div ref={profileRef} className="relative ml-1">
                      <button
                        onClick={() => setProfileOpen(!profileOpen)}
                        className={`flex items-center gap-1.5 p-1 pr-2 rounded-lg transition-all duration-300 ${
                          transparent
                            ? "hover:bg-white/[0.06]"
                            : "hover:bg-slate-100"
                        }`}
                      >
                        <img
                          src={user.avatar}
                          alt=""
                          className={`w-7 h-7 rounded-full object-cover ring-2 ${
                            transparent ? "ring-white/20" : "ring-slate-200"
                          }`}
                        />
                        <FiChevronDown
                          size={12}
                          className={`transition-all duration-200 ${profileOpen ? "rotate-180" : ""} ${
                            transparent ? "text-white/40" : "text-slate-400"
                          }`}
                        />
                      </button>

                      <AnimatePresence>
                        {profileOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 4, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 4, scale: 0.98 }}
                            transition={{ duration: 0.12 }}
                            className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-xl shadow-slate-200/50 py-1 overflow-hidden"
                          >
                            <div className="px-4 py-3 border-b border-slate-100">
                              <p className="text-sm font-bold text-slate-900">
                                {user.name}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {user.email}
                              </p>
                            </div>
                            <Link
                              to="/submit"
                              onClick={() => setProfileOpen(false)}
                              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                            >
                              <FiPlus size={14} /> Submit Website
                            </Link>
                            <button
                              onClick={() => {
                                logout();
                                setProfileOpen(false);
                              }}
                              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <FiLogOut size={14} /> Sign Out
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={openLogin}
                      className={`text-[13px] font-semibold px-3.5 py-1.5 rounded-lg transition-all duration-300 ${
                        transparent
                          ? "text-white/60 hover:text-white hover:bg-white/[0.06]"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      }`}
                    >
                      Sign In
                    </button>
                    <button
                      onClick={openRegister}
                      className={`text-[13px] font-semibold px-3.5 py-1.5 rounded-lg transition-all duration-300 ${
                        transparent
                          ? "text-white bg-white/10 hover:bg-white/20 border border-white/10"
                          : "text-white bg-indigo-600 hover:bg-indigo-700"
                      }`}
                    >
                      Get Started
                    </button>
                  </>
                )}
              </div>

              {/* Mobile icon buttons */}
              <button
                onClick={() => setSearchOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-all ${
                  transparent
                    ? "text-white/60 hover:bg-white/10"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                <FiSearch size={18} />
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`lg:hidden p-2 rounded-lg transition-all ${
                  transparent
                    ? "text-white/70 hover:bg-white/10"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {mobileOpen ? <FiX size={18} /> : <FiMenu size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className={`lg:hidden overflow-hidden ${
                transparent
                  ? "bg-[#0a0f1e]/95 backdrop-blur-xl border-t border-white/[0.06]"
                  : "bg-white border-t border-slate-200"
              }`}
            >
              <div className="container-main py-3 space-y-0.5">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl text-[15px] font-semibold transition-all ${
                      active(l.to)
                        ? transparent
                          ? "bg-white/10 text-white"
                          : "bg-indigo-50 text-indigo-600"
                        : transparent
                          ? "text-white/50 hover:text-white hover:bg-white/5"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    {l.label}
                    <FiArrowRight size={14} className="opacity-40" />
                  </Link>
                ))}

                <div
                  className={`pt-3 mt-2 border-t space-y-2 ${transparent ? "border-white/10" : "border-slate-200"}`}
                >
                  {user ? (
                    <>
                      <div
                        className={`flex items-center gap-3 px-4 py-2 ${transparent ? "text-white/60" : "text-slate-600"}`}
                      >
                        <img
                          src={user.avatar}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover ring-2 ring-slate-200"
                        />
                        <div>
                          <p
                            className={`text-sm font-bold ${transparent ? "text-white/90" : "text-slate-900"}`}
                          >
                            {user.name}
                          </p>
                          <p
                            className={`text-xs ${transparent ? "text-white/40" : "text-slate-500"}`}
                          >
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <Link
                        to="/submit"
                        className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold ${
                          transparent
                            ? "bg-white/15 text-white"
                            : "bg-indigo-600 text-white"
                        }`}
                      >
                        <FiPlus size={14} /> Submit Website
                      </Link>
                      <button
                        onClick={logout}
                        className={`w-full py-2.5 rounded-xl text-sm font-semibold ${
                          transparent
                            ? "text-red-400 hover:bg-white/5"
                            : "text-red-600 hover:bg-red-50"
                        }`}
                      >
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={openLogin}
                        className={`w-full py-2.5 rounded-xl text-sm font-semibold border-2 ${
                          transparent
                            ? "text-white border-white/15 hover:bg-white/10"
                            : "text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Sign In
                      </button>
                      <button
                        onClick={openRegister}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
                      >
                        Get Started
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Search Overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]"
          >
            <div
              onClick={() => setSearchOpen(false)}
              className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm"
            />
            <motion.div
              ref={searchRef}
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-xl mx-4 bg-white rounded-2xl shadow-2xl shadow-slate-900/20 border border-slate-200 overflow-hidden"
            >
              <form onSubmit={handleSearch}>
                <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
                  <FiSearch
                    size={20}
                    className="text-slate-400 flex-shrink-0"
                  />
                  <input
                    type="text"
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search websites, categories, topics..."
                    className="flex-1 text-base text-slate-900 placeholder-slate-400 outline-none bg-transparent font-medium"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <FiX size={16} />
                    </button>
                  )}
                  <kbd className="text-[10px] font-bold text-slate-400 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
                    ESC
                  </kbd>
                </div>
              </form>
              <div className="px-5 py-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Quick links
                </p>
                <div className="space-y-0.5">
                  {[
                    { label: "Browse all websites", to: "/websites" },
                    { label: "View categories", to: "/categories" },
                    { label: "Top 50 rankings", to: "/top-50" },
                    { label: "Submit a website", to: "/submit" },
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setSearchOpen(false)}
                      className="flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors group"
                    >
                      {item.label}
                      <FiArrowRight
                        size={13}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isHome && <div className="h-[3.75rem]" />}
    </>
  );
};

export default Navbar;
